import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Button from 'components/UI/Button';
import ConfimationModal from 'components/UI/ConfimationModal';
import { Option } from 'components/UI/Select';
import { renderToast, toast } from 'components/UI/ToastNotification';
import EditorSection from 'view/EditorSection';
import RecommendationSectionCard from './RecommendationSectionCard';
import RecommendationSectionEditor, { SectionEditorErrors } from './RecommendationSectionEditor';
import { useAPI } from 'lib/API';
import * as Arr from 'lib/Array';
import { RecommendationSection, RecommendationTitle } from 'data/Product';
import { loadRecommendationTitles } from 'requests/productRecommendation';
import { DISPLAY_MODE, RECOMMENDATION_ERRORS, RECOMMENDATION_STRINGS } from 'constants/productRecommendations';
import { Border, Text } from 'styles/themeColors';

const EmptyState = styled.div`
  border: 1px dashed ${Border.main};
  border-radius: 8px;
  padding: 40px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
`;

const EmptyTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${Text.primary};
`;

const EmptySubtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${Text.placeholder};
`;

const EmptyAction = styled.div`
  margin-top: 12px;
`;

// `index` is the saved section being edited, or null for a new one.
interface EditingState {
  index: number | null;
  draft: RecommendationSection;
}

interface Props {
  sections: RecommendationSection[];
  onChange: (sections: RecommendationSection[]) => void;
  onEditingChange: (isEditing: boolean) => void;
  vendorId?: number;
  excludeCode?: string;
  disabled?: boolean;
}

const createEmptySection = (): RecommendationSection => ({
  titleId: null,
  displayMode: DISPLAY_MODE.ORDERED,
  items: [],
});

const validateDraft = (draft: RecommendationSection, others: RecommendationSection[]): SectionEditorErrors => {
  const errors: SectionEditorErrors = {};
  if (!draft.titleId) errors.title = RECOMMENDATION_ERRORS.TITLE_REQUIRED;
  else if (others.some(section => section.titleId === draft.titleId)) errors.title = RECOMMENDATION_ERRORS.DUPLICATE_TITLE;
  if (draft.items.length === 0) errors.products = RECOMMENDATION_ERRORS.PRODUCTS_REQUIRED;
  return errors;
};

/**
 * Sections live in the product form: saving a section only writes it into `sections`, and
 * nothing reaches the API until the product itself is saved. One editor is open at a time;
 * while it is, Add and Edit are disabled and the parent disables product Save/Submit.
 */
const ProductRecommendationsSection = ({ sections, onChange, onEditingChange, vendorId, excludeCode, disabled }: Props) => {
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [errors, setErrors] = useState<SectionEditorErrors>({});
  const [removeIndex, setRemoveIndex] = useState<number | null>(null);

  const titlesReq = useAPI<RecommendationTitle[]>({
    promiseFn: loadRecommendationTitles,
    onReject: () => renderToast(toast.TYPE.ERROR, RECOMMENDATION_STRINGS.TITLES_FAILED),
  });

  const titleOptions: Option[] = useMemo(
    () => (titlesReq.data ?? []).map(title => ({ value: String(title.id), label: title.title })),
    [titlesReq.data]
  );
  const titleById = useMemo(
    () => new Map((titlesReq.data ?? []).map(title => [title.id, title.title])),
    [titlesReq.data]
  );

  const isEditing = editing !== null;

  useEffect(() => {
    onEditingChange(isEditing);
  }, [isEditing, onEditingChange]);

  // A tab switch unmounts the section, which discards the draft; release the parent's lock too.
  useEffect(() => () => onEditingChange(false), [onEditingChange]);

  const openEditor = (index: number | null) => {
    setErrors({});
    setEditing({ index, draft: index === null ? createEmptySection() : sections[index] });
  };

  const closeEditor = () => {
    setErrors({});
    setEditing(null);
  };

  const saveDraft = () => {
    if (!editing) return;
    const others = sections.filter((_, index) => index !== editing.index);
    const nextErrors = validateDraft(editing.draft, others);
    setErrors(nextErrors);
    if (nextErrors.title || nextErrors.products) return;

    onChange(editing.index === null ? [...sections, editing.draft] : Arr.set(sections, editing.index, editing.draft));
    closeEditor();
  };

  const confirmRemove = () => {
    if (removeIndex !== null) onChange(Arr.removeAtIndex(sections, removeIndex));
    setRemoveIndex(null);
  };

  const isEmpty = sections.length === 0 && !isEditing;

  const addButton = (
    <Button id="add-recommendation-section" theme="brandDark" onClick={() => openEditor(null)} disabled={disabled || isEditing}>
      {RECOMMENDATION_STRINGS.ADD_SECTION}
    </Button>
  );

  // One Add action at a time (AC2): inside the empty-state box when there are no sections,
  // in the section header otherwise.
  return (
    <EditorSection
      heading={RECOMMENDATION_STRINGS.SECTION_HEADING}
      infoIconText={RECOMMENDATION_STRINGS.SECTION_TOOLTIP}
      actions={isEmpty ? undefined : addButton}
    >
      {isEmpty && (
        <EmptyState>
          <EmptyTitle>{RECOMMENDATION_STRINGS.EMPTY_TITLE}</EmptyTitle>
          <EmptySubtitle>{RECOMMENDATION_STRINGS.EMPTY_SUBTITLE}</EmptySubtitle>
          <EmptyAction>{addButton}</EmptyAction>
        </EmptyState>
      )}

      {sections.map((section, index) =>
        editing?.index === index ? (
          <RecommendationSectionEditor
            key={section.id ?? `section-${index}`}
            draft={editing.draft}
            isNew={false}
            titleOptions={titleOptions}
            errors={errors}
            onChange={draft => setEditing({ index, draft })}
            onSave={saveDraft}
            onCancel={closeEditor}
            vendorId={vendorId}
            excludeCode={excludeCode}
          />
        ) : (
          <RecommendationSectionCard
            key={section.id ?? `section-${index}`}
            index={index}
            section={section}
            title={section.titleId ? titleById.get(section.titleId) ?? '' : ''}
            onEdit={() => openEditor(index)}
            onRemove={() => setRemoveIndex(index)}
            disabled={disabled || isEditing}
          />
        )
      )}

      {editing?.index === null && (
        <RecommendationSectionEditor
          draft={editing.draft}
          isNew
          titleOptions={titleOptions}
          errors={errors}
          onChange={draft => setEditing({ index: null, draft })}
          onSave={saveDraft}
          onCancel={closeEditor}
          vendorId={vendorId}
          excludeCode={excludeCode}
        />
      )}

      <ConfimationModal
        show={removeIndex !== null}
        onClose={() => setRemoveIndex(null)}
        onConfirm={confirmRemove}
        title={RECOMMENDATION_STRINGS.REMOVE_CONFIRM_TITLE}
        message={RECOMMENDATION_STRINGS.REMOVE_CONFIRM_MESSAGE}
        confirmText={RECOMMENDATION_STRINGS.REMOVE}
      />
    </EditorSection>
  );
};

export default ProductRecommendationsSection;
